import { pageNavigationIssue } from '@/components/Navigation/pages'
// 声明式页面导航只处理白名单参数，不执行脚本、不请求设备接口。
export const contextKeys = ['productId', 'deviceId']
const own = (value, key) => value && Object.prototype.hasOwnProperty.call(value, key)
const plain = value => value && typeof value === 'object' && !Array.isArray(value)
const identifier = value => (typeof value === 'string' && value.trim() && value.length <= 256)
  || (typeof value === 'number' && Number.isFinite(value))

export function cleanContext (value = {}) {
  const result = {}
  contextKeys.forEach(key => { if (own(value, key) && identifier(value[key])) result[key] = String(value[key]).trim() })
  return result
}

export function dataField (data, path) {
  if (typeof path !== 'string' || !/^[a-zA-Z_$][\w$]*(\.[a-zA-Z_$][\w$]*)*$/.test(path)) return undefined
  let value = data
  for (const key of path.split('.')) {
    if (['__proto__', 'prototype', 'constructor'].includes(key) || !own(value, key)) return undefined
    value = value[key]
  }
  return value
}

// 新规则多选产品；旧单产品规则仍按原编号解析，不隐式回退无效的新字段。
export function productRouteIds (route) {
  if (!plain(route)) return []
  if (own(route, 'productIds')) return Array.isArray(route.productIds) ? route.productIds : []
  return own(route, 'productId') ? [route.productId] : []
}

export function navigationIssue (action, pages) {
  if (!action || action.type === 'none' || action.type === 'back') return ''
  if (action.type === 'pages') return pageNavigationIssue(action, pages)
  if (action.type === 'product') {
    if (!Array.isArray(action.routes)) return '产品跳转规则必须是列表'
    const products = new Set()
    for (let i = 0; i < action.routes.length; i++) {
      const route = action.routes[i], prefix = '产品规则' + (i + 1) + '：'
      const ids = productRouteIds(route)
      if (!ids.length) return prefix + '请至少选择一个有效产品'
      for (const value of ids) {
        if (typeof value !== 'string' || !identifier(value)) return prefix + '请至少选择一个有效产品'
        const id = value.trim()
        if (products.has(id)) return prefix + '产品重复，每个产品只能属于一条规则'
        products.add(id)
      }
      if (!pages.some(page => page.id === route.pageId)) return prefix + '请选择有效的目标页面；原页面可能已被删除'
    }
    const fallback = action.fallback === undefined ? { type: 'none' } : action.fallback
    if (!plain(fallback) || !['none', 'page'].includes(fallback.type)) return '请选择有效的未配置产品动作'
    if (fallback.type === 'page' && !pages.some(page => page.id === fallback.pageId)) return '请选择有效的默认页面；原页面可能已被删除'
    return ''
  }
  if (action.type !== 'page') return '不支持的页面交互，请重新选择动作'
  if (!pages.some(page => page.id === action.pageId)) return '请选择有效的目标页面；原页面可能已被删除'
  for (const key of contextKeys) {
    const rule = action.params && action.params[key]
    if (!rule) continue
    if (!plain(rule) || !['inherit', 'literal', 'data', 'row', 'clear'].includes(rule.source)) return key + ' 的参数来源无效'
    if (rule.source === 'literal' && !identifier(rule.value)) return '请填写 ' + key + '（最多256个字符）'
    if (['data', 'row'].includes(rule.source) && (typeof rule.field !== 'string' || !/^[a-zA-Z_$][\w$]*(\.[a-zA-Z_$][\w$]*)*$/.test(rule.field)
      || rule.field.split('.').some(part => ['__proto__', 'constructor', 'prototype'].includes(part)))) return '请填写有效的 ' + key + ' 静态数据字段'
  }
  return ''
}

// 按产品编号选目标页，参数固定来自当前行；不执行表达式，也不继承旧设备。
export function productPageAction (action, row, kind = 'product') {
  const device = kind === 'device', productId = row && (device ? row.productId : row.id)
  if (!identifier(productId)) throw new Error('当前列表行没有有效的产品编号，页面未切换')
  if (device && !identifier(row.id)) throw new Error('当前列表行没有有效的设备编号，页面未切换')
  const id = String(productId).trim()
  const route = Array.isArray(action.routes) && action.routes.find(item => productRouteIds(item).some(value => typeof value === 'string' && value.trim() === id))
  const target = route || action.fallback
  if (!target || (!route && target.type === 'none')) return { type: 'none' }
  return { type: 'page', pageId: target.pageId, params: {
    productId: { source: 'row', field: device ? 'row.productId' : 'row.id' },
    deviceId: device ? { source: 'row', field: 'row.id' } : { source: 'clear' }
  } }
}

export function navigationEntry (pageId) {
  return { pageId, context: {}, trail: [] }
}

export function navigatePage (project, state, action, data, origin = {}, options = {}) {
  const pages = project.pages || []
  const issue = navigationIssue(action, pages)
  if (issue) throw new Error(issue)
  if (action && action.type === 'pages') throw new Error('请点击一个有效导航项')
  if (action && action.type === 'product') action = productPageAction(action, dataField(data, 'row'), options.listKind)
  if (!action || action.type === 'none') return state
  if (action.type === 'back') {
    if (!state.trail.length) return state
    const previous = state.trail[state.trail.length - 1]
    if (!pages.some(page => page.id === previous.pageId)) throw new Error('上级页面已不存在，请重新打开项目')
    return { pageId: previous.pageId, context: cleanContext(previous.context), trail: state.trail.slice(0, -1), restore: previous.origin }
  }
  const context = cleanContext(state.context)
  const params = action.params || {}
  // 切换产品时清除旧设备，避免将A产品的设备带到B产品。
  for (const key of contextKeys) {
    const rule = params[key] || { source: 'inherit' }
    if (rule.source === 'clear') delete context[key]
    if (['literal', 'data', 'row'].includes(rule.source)) {
      const value = rule.source === 'literal' ? rule.value : dataField(data, rule.field)
      if (!identifier(value)) throw new Error(key + ' 未取得有效值，页面未切换；请检查交互参数')
      context[key] = String(value).trim()
    }
    if (key === 'productId' && context.productId !== cleanContext(state.context).productId) delete context.deviceId
  }
  if (state.pageId === action.pageId && JSON.stringify(context) === JSON.stringify(cleanContext(state.context))) return state
  // 顶层导航为同级切换，不累积100层访问栈；进入产品/设备详情仍走原有返回链。
  if (options.replace === true) return { pageId: action.pageId, context, trail: [] }
  if (state.trail.length >= 100) throw new Error('页面访问层级已达到100层，请先返回上级')
  return {
    pageId: action.pageId, context,
    trail: [...state.trail, { pageId: state.pageId, context: cleanContext(state.context), origin }]
  }
}
